<template>
  <q-page>
    <q-pull-to-refresh @refresh="refresh">
      <div class="col q-mx-sm">
        <q-list separator>
          <template v-if="loadingData">
            <q-item v-for="n in 5" :key="'skeleton-' + n">
              <q-item-section avatar>
                <q-skeleton type="QAvatar" size="40px" />
              </q-item-section>
              <q-item-section>
                <q-skeleton type="text" class="text-h6" />
                <q-skeleton type="text" width="60%" />
              </q-item-section>
              <q-item-section side>
                <q-skeleton type="QBadge" />
              </q-item-section>
            </q-item>
          </template>
          <q-item v-if="!missions.length && !loadingData">
            <q-item-section>
              <div class="text-h6 text-primary text-center">Keine passenden Einträge gefunden.</div>
            </q-item-section>
          </q-item>
          <template v-for="mission in missions" :key="mission" >
            <mission-list-item :mission="mission" details badges />
          </template>
        </q-list>
      </div>
    </q-pull-to-refresh>
  </q-page>
  <q-footer>
    <q-toolbar v-if="!$route.meta.hideFilters" class="bg-secondary">
      <q-toolbar-title class="text-body1">{{ filters[currentFilter].name }} <q-badge color="primary">{{ missions.length }}</q-badge></q-toolbar-title>
        <q-btn-dropdown
        fab-mini
        auto-close
        dropdown-icon="more_vert"
        unelevated
        >
          <q-list>
            <q-item
            clickable
            draggable=false
            @click="download"
            >
              <q-item-section avatar>
                <q-icon name="download"/>
              </q-item-section>
              <q-item-section>Als ICS exportieren
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
    </q-toolbar>
    <q-toolbar v-if="$route.meta.showCancel" class="q-py-sm">
      <q-btn
          label="Abbrechen"
          icon="cancel"
          color="secondary"
          push
          @click="cancel"
          />
    </q-toolbar>
    <q-toolbar v-if="!$route.meta.hideFilters" class="text-primary q-py-sm q-gutter-sm">
      <q-btn
          push
          icon="filter_list"
          color="secondary"
          class="ellipsis"
          label="filter"
          >
          <q-menu>
            <q-list>
              <template v-for="filter in filters" :key="filter">
                <q-item v-if="filter.selectable" clickable v-close-popup @click="filter.filter">
                  <q-item-section>
                    <q-item-label>{{filter.name}}</q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-list>
          </q-menu>
      </q-btn>
      <q-btn
          push
          to="/missions/new"
          v-if="canAddMission"
          label="Neuer Termin"
          icon="event"
          color="secondary"
          />
    </q-toolbar>
  </q-footer>
</template>

<script>
import MissionListItem from '@/components/MissionListItem.vue'
import { downloadMissionAsIcal } from '@/services/Application.js'
import { hasPermission } from '@/services/Users.js'
import { 
  getMissionsUpcoming,
  getMissionsByYear,
  getMissionsByYearAndMember,
  getShiftableMissionsForMember,
  createMission
  } from '@/services/Missions.js'

import { apiErrorHandler } from '@/utils/apiErrorHandler.js'
import { byDate } from '@/utils/useDateUtils.js'
import { useUserStore } from '@/stores/user.js'
import { useAppStore } from '@/stores/application.js'

export default {
  setup() {
    const userStore = useUserStore()
    const appStore = useAppStore()
    return {
      userStore,
      appStore
    }
  },
  components: {
    'mission-list-item': MissionListItem
    },
  data() {
    return {
        missions: [],
        filters: [
          { name: "Zukünftige Termine", filter: this.filter_upcoming, selectable: true },
          { name: "Alle Termine", filter: this.filter_all, selectable: true },
          { name: "Meine Termine", filter: this.filter_my, selectable: true },
          { name: "Tauschtermine", filter: this.filter_shiftable, selectable: false }
        ],
        currentFilter: 0,
        showCreateForm: false,
        loadingData: false
    }
  },
  mounted(){
    this.missions = this.appStore.missionData
    if(!this.missions.length || this.$route.meta.filterIndex) {
      this.fetch_filtered()
    }
  },
  computed: {
    canAddMission() {
      return hasPermission("missions.add_mission")
    },
  },
  methods: {
    download() {
      downloadMissionAsIcal(this.missions)
    },
    fetch_filtered() {
      if(this.$route.meta.filterIndex) {
        this.filters[this.$route.meta.filterIndex].filter() 
      }
      else {
        let index = this.appStore.missionFilter || this.currentFilter
        this.filters[index].filter() 
      }
    },
    filter_my() {
      this.currentFilter=2
      this.appStore.setMissionFilter(2)
      this.loadingData=true

      getMissionsByYearAndMember(new Date().getFullYear(), this.userStore.data.id)
        .then( data => {
          this.missions = data.sort(byDate)
          this.appStore.setMissionData(this.missions)
          })
        .catch(apiErrorHandler("[MissionList] Fehler beim Filtern (meine)"))
        .finally(() => {
          this.loadingData = false
        })
    },
    filter_all() {
      this.currentFilter=1
      this.appStore.setMissionFilter(1)
      this.loadingData=true

      getMissionsByYear(new Date().getFullYear())
        .then( data => { 
          this.missions = data.sort(byDate)
          this.appStore.setMissionData(this.missions)
          })
        .catch(apiErrorHandler("[MissionList] Fehler beim Filtern (alle)"))
        .finally(() => {
          this.loadingData = false
        })
    },
    filter_upcoming() {
      this.currentFilter=0
      this.appStore.setMissionFilter(0)
      this.loadingData=true

      getMissionsUpcoming()
      .then( data => {
          this.missions = data.sort(byDate)
          this.appStore.setMissionData(this.missions)
        })
        .catch(apiErrorHandler("[MissionList] Fehler beim Filtern (zukünftige)"))
        .finally(() => {
          this.loadingData = false
        })
    },
    filter_shiftable() {
        this.loadingData=true
        getShiftableMissionsForMember(this.userStore.data.id)
           .then( data => {
          this.missions = data.sort(byDate)
          this.appStore.setMissionData(this.missions)
           })
          .catch(apiErrorHandler("[MissionList] Fehler beim Filtern (Tausch)"))
          .finally(() => {
            this.loadingData = false
          })
    },
    refresh(done)
    {
      this.fetch_filtered()
      done()
    },
    cancel() {
        this.appStore.clearAction()
        this.$router.back()
    },
    createMission(mission) {
        createMission(mission)
        .then( response => this.$router.replace(`/missions/${response.id}`))
        .catch(apiErrorHandler("[MissionList] Fehler beim Erstellen des Termins"))
    }
  }
}
</script>

<style>
.breadcrumbs {
  font-size: 16px;
}
#mission-filter-label {
  width: 15ch;
  overflow: ellipsis;
}
</style>
